<template>
    <div v-if="loaded">
        <card-header icon="fa-clipboard-list-check" :title="plan.course_title"/>

        <card-body>
            <card-list>
                <!-- Student Information -->
                <subheader-list-item title="Student Information" icon="fa-user"/>
                <data-row title="Name">{{ plan.first_name }} {{ plan.last_name }}</data-row>
                <data-row title="Email">{{ plan.email }}</data-row>

                <!-- Enrollment Details -->
                <subheader-list-item title="Enrollment Details" icon="fa-info-circle"/>
                <data-row title="Course">{{ plan.course_title }}</data-row>
                <data-row title="Enrolled Hours">{{ enrolledHours }}</data-row>
                <data-row title="Enrolled">{{ plan.enrolled_at | moment('MMMM Do, YYYY') }}</data-row>
                <data-row title="Submitted">{{ setupData.submitted_at | moment('MMMM Do, YYYY [at] h:mm A') }}</data-row>

                <!-- Resume Section -->
                <subheader-list-item title="Resume" icon="fa-file-user"/>
                <container-list-item padded>
                    <div class="mb-4">
                        <label class="label is-size-7 has-text-grey">Professional Background</label>
                        <p class="content-text">{{ setupData.resume.background }}</p>
                    </div>
                    <div class="mb-4">
                        <label class="label is-size-7 has-text-grey">Work History</label>
                        <p class="content-text">{{ setupData.resume.work_history }}</p>
                    </div>
                    <div>
                        <label class="label is-size-7 has-text-grey">Why This Subject Matters</label>
                        <p class="content-text">{{ setupData.resume.importance }}</p>
                    </div>
                </container-list-item>

                <!-- Goals -->
                <subheader-list-item title="Goals" icon="fa-bullseye"/>
                <container-list-item padded>
                    <ul class="goal-list">
                        <li v-for="(goal, index) in allGoals" :key="'goal-' + index">
                            {{ goal }}
                        </li>
                    </ul>
                </container-list-item>

                <!-- Objectives with Hour Allocations -->
                <subheader-list-item title="Objectives & Hour Allocations" icon="fa-list-check"/>
                <container-list-item padded>
                    <div v-for="(obj, index) in objectivesWithHours" :key="'obj-' + index" class="objective-item mb-4">
                        <div class="objective-text">{{ obj.text }}</div>
                        <div class="objective-hours mt-2">
                            <div class="columns is-mobile is-vcentered">
                                <div class="column">
                                    <progress class="progress is-info is-small" :value="obj.hours" :max="enrolledHours">
                                        {{ obj.hours }} hours
                                    </progress>
                                </div>
                                <div class="column is-narrow">
                                    <span class="has-text-weight-bold">{{ obj.hours }}</span>
                                    <span class="is-size-7 has-text-grey"> hours</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </container-list-item>

                <!-- Application Levels -->
                <subheader-list-item title="Application Levels" icon="fa-layer-group"/>
                <container-list-item padded>
                    <div class="tags">
                        <span v-for="level in setupData.application_level" :key="level" class="tag is-info is-medium">
                            {{ formatLevel(level) }}
                        </span>
                    </div>
                </container-list-item>

                <!-- Additional Materials -->
                <template v-if="setupData.other_materials">
                    <subheader-list-item title="Additional Materials" icon="fa-book"/>
                    <container-list-item padded>
                        <p class="content-text">{{ setupData.other_materials }}</p>
                    </container-list-item>
                </template>

                <!-- Evaluation Plan -->
                <subheader-list-item title="Evaluation Plan" icon="fa-clipboard-check"/>
                <container-list-item padded>
                    <p class="content-text">{{ setupData.evaluation_plan }}</p>
                </container-list-item>

                <!-- Custom Course Name -->
                <template v-if="plan && plan.custom_name">
                    <subheader-list-item title="Custom Course Name" icon="fa-tag"/>
                    <data-row title="Name">{{ plan.custom_name }}</data-row>
                </template>

                <!-- Reflections Agreement -->
                <data-row title="Reflections Agreement">
                    <span v-if="setupData.reflections_agreed" class="has-text-success">
                        <i class="fas fa-check mr-1"></i> Agreed
                    </span>
                    <span v-else class="has-text-danger">
                        <i class="fas fa-times mr-1"></i> Not Agreed
                    </span>
                </data-row>

                <!-- STEM Clock Hours opt-in -->
                <data-row v-if="setupData.stem_agreed" title="STEM Clock Hours">
                    <span class="has-text-success">
                        <i class="fas fa-flask mr-1"></i> Opted in &mdash; reflections must address the OSPI STEM requirements
                    </span>
                </data-row>

                <!-- AI Generation Section -->
                <subheader-list-item title="AI-Generated Content" icon="fa-robot"/>
                <container-list-item padded>
                    <b-field label="Additional Guidance (Optional)">
                        <b-input
                            type="textarea"
                            v-model="additionalGuidance"
                            placeholder="Provide any additional guidance for the AI to consider when generating the syllabus and first milestone..."
                            rows="3"
                        />
                    </b-field>

                    <div class="buttons mt-4">
                        <b-button
                            type="is-info"
                            size="is-small"
                            @click="generateContent"
                            :loading="generating"
                            :disabled="generating"
                        >
                            <i class="fas fa-magic mr-2"></i>
                            {{ hasGeneratedContent ? 'Regenerate Content' : 'Generate Content' }}
                        </b-button>
                    </div>

                    <!-- Generated Content Preview -->
                    <template v-if="hasGeneratedContent">
                        <b-tabs v-model="activeTab" class="mt-4">
                            <b-tab-item label="Syllabus" icon="fa-book-open">
                                <div class="ai-content" v-html="generatedSyllabus"></div>
                            </b-tab-item>
                            <b-tab-item label="First Milestone" icon="fa-flag-checkered">
                                <div class="ai-content" v-html="generatedMilestone"></div>
                            </b-tab-item>
                        </b-tabs>
                    </template>

                    <template v-else-if="!generating">
                        <div class="notification is-light mt-4">
                            <p class="has-text-grey">
                                <i class="fas fa-info-circle mr-2"></i>
                                Click "Generate Content" to create a syllabus and first milestone for this student based on their study plan.
                            </p>
                        </div>
                    </template>
                </container-list-item>
            </card-list>
        </card-body>

        <card-footer>
            <div class="columns">
                <div class="column">
                    <b-button type="is-warning" size="is-small" @click="openReject">
                        <i class="fas fa-comment-dots mr-2"></i>
                        Request Changes
                    </b-button>
                </div>
                <div class="column has-text-right">
                    <b-button
                        type="is-success"
                        size="is-small"
                        @click="approve"
                        :loading="approving"
                        :disabled="!hasGeneratedContent"
                    >
                        <i class="fas fa-check mr-2"></i>
                        Approve & Activate
                    </b-button>
                </div>
            </div>
        </card-footer>
    </div>
</template>

<script>
    import CardHeader from "@/TIER/components/CardHeader";
    import CardBody from "@/TIER/components/CardBody";
    import CardFooter from "@/TIER/components/CardFooter";
    import CardList from "@/TIER/components/CardList";
    import SubheaderListItem from "@/TIER/components/SubheaderListItem";
    import ContainerListItem from "@/TIER/components/ContainerListItem";
    import DataRow from "@/TIER/components/DataRow";
    import {client as http} from "@/http_client";
    import {mapActions} from "vuex";

    export default {
        components: {DataRow, ContainerListItem, SubheaderListItem, CardList, CardFooter, CardBody, CardHeader},
        props: ['card', 'props'],
        data() {
            return {
                plan: null,
                setupData: null,
                courseConfig: null,
                loaded: false,
                approving: false,
                generating: false,
                additionalGuidance: '',
                generatedSyllabus: null,
                generatedMilestone: null,
                activeTab: 0
            };
        },
        computed: {
            enrolledHours() {
                return this.plan?.enrolled_hours || this.courseConfig?.enrolled_hours || 20;
            },
            hasGeneratedContent() {
                return this.generatedSyllabus && this.generatedMilestone;
            },
            allGoals() {
                const goals = [];
                // Get text for selected goals
                if (this.setupData.selected_goals && this.courseConfig?.goals) {
                    let configGoals = this.courseConfig.goals;
                    if (typeof configGoals === 'string') {
                        try {
                            configGoals = JSON.parse(configGoals);
                        } catch (e) {
                            configGoals = configGoals.split('\n').filter(g => g.trim());
                        }
                    }
                    this.setupData.selected_goals.forEach(id => {
                        const goal = Array.isArray(configGoals)
                            ? configGoals.find(g => (g.id || g) === id)
                            : null;
                        goals.push(goal?.text || goal || id);
                    });
                }
                // Add custom goals
                if (this.setupData.custom_goals) {
                    goals.push(...this.setupData.custom_goals.filter(g => g.trim()));
                }
                return goals;
            },
            objectivesWithHours() {
                const objectives = [];
                // Get text for selected objectives
                if (this.setupData.selected_objectives && this.courseConfig?.objectives) {
                    let configObjectives = this.courseConfig.objectives;
                    if (typeof configObjectives === 'string') {
                        try {
                            configObjectives = JSON.parse(configObjectives);
                        } catch (e) {
                            configObjectives = configObjectives.split('\n').filter(o => o.trim());
                        }
                    }
                    this.setupData.selected_objectives.forEach(id => {
                        const obj = Array.isArray(configObjectives)
                            ? configObjectives.find(o => (o.id || o) === id)
                            : null;
                        objectives.push({
                            id: id,
                            text: obj?.text || obj || id,
                            hours: this.setupData.hour_allocations?.[id] || 0
                        });
                    });
                }
                // Add custom objectives
                if (this.setupData.custom_objectives) {
                    this.setupData.custom_objectives.filter(o => o.trim()).forEach((text, index) => {
                        const id = 'custom_' + index;
                        objectives.push({
                            id: id,
                            text: text,
                            hours: this.setupData.hour_allocations?.[id] || 0
                        });
                    });
                }
                return objectives;
            }
        },
        methods: {
            ...mapActions(['getStudyPlansCount']),
            formatLevel(level) {
                const levels = {
                    'awareness': 'Awareness',
                    'beginning': 'Beginning',
                    'developing': 'Developing',
                    'applying': 'Applying',
                    'integrating': 'Integrating'
                };
                return levels[level] || level;
            },
            load() {
                this.$emit('loading', true);
                http.get(`/study-plans/${this.props.enrollmentId}`, {force: true}).then(response => {
                    this.plan = response.data;
                    this.setupData = response.data.setup_data || {};
                    this.courseConfig = response.data.course_config || {};

                    // Load any previously generated content
                    if (this.setupData.syllabus) {
                        this.generatedSyllabus = this.setupData.syllabus;
                    }
                    if (this.setupData.first_milestone) {
                        this.generatedMilestone = this.setupData.first_milestone;
                    }
                    if (this.setupData.additional_guidance) {
                        this.additionalGuidance = this.setupData.additional_guidance;
                    }

                    this.loaded = true;
                    this.$emit('loading', false);
                }).catch(err => {
                    console.error('Error loading study plan:', err);
                    this.$emit('loading', false);
                    this.$buefy.toast.open({
                        message: 'Error loading study plan',
                        type: 'is-danger'
                    });
                });
            },
            generateContent() {
                this.generating = true;
                http.post(`/study-plans/${this.props.enrollmentId}/generate`, {
                    additional_guidance: this.additionalGuidance
                }).then(response => {
                    this.generatedSyllabus = response.data.syllabus;
                    this.generatedMilestone = response.data.first_milestone;
                    this.$buefy.toast.open({
                        message: 'Content generated successfully',
                        type: 'is-success'
                    });
                }).catch(err => {
                    console.error('Error generating content:', err);
                    this.$buefy.toast.open({
                        message: err.response?.data?.error || 'Error generating content',
                        type: 'is-danger'
                    });
                }).finally(() => {
                    this.generating = false;
                });
            },
            openReject() {
                this.$openCard('study-plan-reject', {enrollmentId: this.props.enrollmentId}, this.card);
            },
            approve() {
                if (!this.hasGeneratedContent) {
                    this.$buefy.toast.open({
                        message: 'Please generate content before approving',
                        type: 'is-warning'
                    });
                    return;
                }

                this.$buefy.dialog.confirm({
                    title: 'Approve Study Plan',
                    message: 'Are you sure you want to approve this study plan? The student will be notified and can begin the course.',
                    confirmText: 'Approve',
                    type: 'is-success',
                    onConfirm: () => {
                        this.approving = true;
                        http.post(`/study-plans/${this.props.enrollmentId}/approve`, {
                            syllabus: this.generatedSyllabus,
                            first_milestone: this.generatedMilestone,
                            additional_guidance: this.additionalGuidance
                        }).then(() => {
                            this.$buefy.toast.open({
                                message: 'Study plan approved',
                                type: 'is-success'
                            });
                            this.getStudyPlansCount();
                            this.$closeCard(this.card);
                            this.$reloadCard('study-plans');
                        }).catch(err => {
                            console.error('Error approving study plan:', err);
                            this.$buefy.toast.open({
                                message: 'Error approving study plan',
                                type: 'is-danger'
                            });
                        }).finally(() => {
                            this.approving = false;
                        });
                    }
                });
            }
        },
        watch: {
            'props.enrollmentId': function() {
                this.load();
            }
        },
        mounted() {
            this.load();
        }
    };
</script>

<style lang="scss" scoped>
    .content-text {
        white-space: pre-wrap;
        line-height: 1.6;
    }

    .goal-list {
        margin-left: 1.5rem;

        li {
            margin-bottom: 0.5rem;
        }
    }

    .objective-item {
        padding-bottom: 1rem;
        border-bottom: 1px solid #eee;

        &:last-child {
            border-bottom: none;
            padding-bottom: 0;
        }
    }

    .objective-text {
        font-weight: 500;
    }

    .progress {
        margin-bottom: 0;
    }

    .tags .tag {
        text-transform: capitalize;
    }

    .ai-content {
        padding: 1rem;
        background: #fafafa;
        border-radius: 4px;

        ::v-deep {
            h4, h5 {
                margin-top: 1rem;
                margin-bottom: 0.5rem;
                color: #363636;
            }

            ul, ol {
                margin-left: 1.5rem;
                margin-bottom: 1rem;
            }

            li {
                margin-bottom: 0.25rem;
            }

            p {
                margin-bottom: 0.75rem;
            }
        }
    }
</style>
